import { bootstrapApplication } from "@angular/platform-browser";
import { provideRouter } from "@angular/router";
import { AppComponent } from "./app/app.component";
import { AppRoutes } from "./app/app.routes";
import { jwtDecode } from "jwt-decode";
import { appProviders } from "./app/shared/services/app.providers";
import { provideStore } from "@ngrx/store";
import { appReducers } from "@shared/store/app.reducers";
import { metaReducers } from "@shared/store/utils";
import { provideEffects } from "@ngrx/effects";
import { AuthEffects } from "@shared/store/auth/auth.effects";
import { ENVIRONMENT_INITIALIZER, inject, Injector, importProvidersFrom } from "@angular/core";
import { setAppInjector } from "./app/app.injector";

import {provideHttpClient, HttpClient} from "@angular/common/http";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {provideTranslateHttpLoader} from "@ngx-translate/http-loader";



// Early bootstrap hook: if URL contains impersonate_token, store it in sessionStorage
(function initImpersonationFromUrl() {
  try {
    const url = new URL(window.location.href);
    const token = url.searchParams.get("impersonate_token");
    if (token) {
      sessionStorage.setItem("userToken", token);
      sessionStorage.setItem("userRefreshToken", token);
      sessionStorage.setItem("isImpersonated", "true");

      // Try to decode orgId and persist it for headers
      try {
        const decoded: any = jwtDecode(token);
        const orgId =
          decoded?.oid ||
          decoded?.organisationId ||
          decoded?.orgId ||
          decoded?.oi;
        if (orgId) {
          sessionStorage.setItem("orgId", orgId);
        }
      } catch {
      }

      // Write a basic handoff so other new tabs can restore impersonation
      try {
        const handoff = {
          token,
          refreshToken: token,
          ts: Date.now(),
          orgId: sessionStorage.getItem("orgId") || null,
        };
        localStorage.setItem("impersonationHandoff", JSON.stringify(handoff));
      } catch {
      }

      // Remove token from URL to avoid re-processing and leaking token via referrer
      url.searchParams.delete("impersonate_token");
      // Keep other params like force_reload, but we can drop it too to avoid reload loops
      url.searchParams.delete("force_reload");
      window.history.replaceState({}, document.title, url.toString());
    }
  } catch {
    // no-op
  }
})();

// Compatibility shim: In impersonated tabs, redirect localStorage orgId reads/writes to sessionStorage
(function shimOrgIdStorageForImpersonation() {
  try {
    const isImpersonated = sessionStorage.getItem("isImpersonated") === "true";
    if (!isImpersonated) return;

    const ls = window.localStorage;
    const originalGetItem = ls.getItem.bind(ls);
    const originalSetItem = ls.setItem.bind(ls);
    const originalRemoveItem = ls.removeItem.bind(ls);

    ls.getItem = (key: string): string | null => {
      if (key === "orgId") {
        return sessionStorage.getItem("orgId");
      }
      return originalGetItem(key);
    };

    ls.setItem = (key: string, value: string): void => {
      if (key === "orgId") {
        sessionStorage.setItem("orgId", value);
        return;
      }
      originalSetItem(key, value);
    };

    ls.removeItem = (key: string): void => {
      if (key === "orgId") {
        sessionStorage.removeItem("orgId");
        return;
      }
      originalRemoveItem(key);
    };
  } catch {
    // no-op
  }
})();

bootstrapApplication(AppComponent, {
  providers: [
    ...appProviders,
    provideRouter(AppRoutes),
    provideStore(appReducers, { metaReducers }),
    provideEffects(AuthEffects),
    provideHttpClient(),
    importProvidersFrom(
      TranslateModule.forRoot({
        defaultLanguage: 'en',
      })
    ),
    ...provideTranslateHttpLoader({
      prefix: '/assets/i18n/',
      suffix: '.json',
    }),
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      // useFactory: () => () => setAppInjector(inject(Injector)),
      useFactory: () => () => {
        const injector = inject(Injector);
        setAppInjector(injector);

        // ✅ get TranslateService
        const translate = injector.get(TranslateService);


        // ✅ read saved language from localStorage (fallback to 'en')
        const savedLang = localStorage.getItem("lang") || "en";

        // ✅ set it on startup
        translate.use(savedLang);
      },
    },
  ],
}).catch((err) => {
  console.error(err);
});